import {
  ReviewsApi,
  type ReviewWithReplyDto,
} from '@up2stay/up2promo-api-client';

type BulkOutcome = {
  status?: 'pending' | 'completed' | 'failed';
  batchId?: string;
  results?: Array<{
    reviewId: string;
    success?: boolean;
    error?: string;
  }>;
  error?: string;
};

export type BulkReplyDraftResult = {
  reviewId: string;
  success: boolean;
  error?: string;
};

/** Group selected reviews by location and create/poll bulk reply drafts. */
export async function createBulkReplyDraftsForReviews(options: {
  workspaceId: string;
  reviewIds: string[];
  reviews: ReviewWithReplyDto[];
}): Promise<BulkReplyDraftResult[]> {
  const { workspaceId, reviewIds, reviews } = options;
  const byLocation = new Map<string, string[]>();
  for (const id of reviewIds) {
    const review = reviews.find((item) => item.id === id);
    if (!review) continue;
    const group = byLocation.get(review.locationId) ?? [];
    group.push(id);
    byLocation.set(review.locationId, group);
  }

  const results: BulkReplyDraftResult[] = [];

  for (const [locationId, ids] of byLocation.entries()) {
    const res = await ReviewsApi.createBulkReplyDrafts({
      path: { workspaceId, locationId },
      body: { reviewIds: ids },
    });
    let outcome = res.data as BulkOutcome;

    if (outcome?.status === 'pending' && outcome.batchId) {
      const batchId = outcome.batchId;
      const deadline = Date.now() + 120_000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 1_000));
        const statusRes = await ReviewsApi.getBulkReplyDraftBatch({
          path: { workspaceId, locationId, batchId },
        });
        outcome = statusRes.data as BulkOutcome;
        if (outcome?.status === 'completed' || outcome?.status === 'failed') {
          break;
        }
      }
    }

    if (outcome?.status === 'failed') {
      throw new Error(outcome.error ?? 'bulk draft batch failed');
    }

    const batch = outcome?.results ?? [];
    results.push(
      ...batch.map((item) => ({
        reviewId: item.reviewId,
        success: item.success !== false && !item.error,
        error: item.error,
      })),
    );
  }

  return results;
}
